import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useWaWorkplaceService from 'src/api/wa/service/WaWorkplaceService';
import {
  OperationStatus,
  operationStatusCodes,
  sortStatusOptions,
  defaultSatusOrder,
} from 'src/api/wa/store/models/workplace/OperationStatus';
import { useWaFilterStore } from 'src/api/wa/store/WaFilterStore';
import { useWaUserStore } from 'src/api/wa/store/WaUserStore';
import { defaultMapCenter } from 'src/common/location/defaultMapSettings';
import Button from 'src/ui/wards/common/button/Button';
import { FormRow } from 'src/ui/wards/common/form/FormRow';
import Input from 'src/ui/wards/common/input/Input';
import DropdownSelect, { OptionProps } from 'src/ui/wards/common/select/DropdownSelect';
import { WaScreensMap } from 'src/ui/wards/router/WardsScreenMap';
import { wardsNavigate } from 'src/ui/wards/router/WardsScreenRegistration';
import { WA_Screen } from 'src/ui/wards/router/WardsScreens';

export default function S3_2_SearchFilter() {
  const { t } = useTranslation();
  const {
    fetchBranchOptions,
    fetchSalesOfficeOptions,
    fetchSectionOptions,
    fetchSquadOptions,
    fetchStatusOptions,
  } = useWaWorkplaceService();
  const {
    branchOptions,
    salesOfficeOptions,
    sectionOptions,
    squadOptions,
    workplaceFilterParameters,
    patchWorkplaceFilterParameters,
    mapCenter,
    setMapCenter,
    setSalesOfficeOptions,
    setSectionOptions,
    setSquadOptions,
    statusOptions,
    filtersInitialized,
    setFiltersInitialized,
    mapCenterUserLocation,
    setMapCenterUserLocation,
    userOfficeLocation,
    setUserOfficeLocation,
    setFilterChanged,
  } = useWaFilterStore();
  const initialFilterParamsRef = useRef({ ...workplaceFilterParameters });
  const initialMapCenterRef = useRef(mapCenter);
  const initialMapCenterUserLocationRef = useRef(mapCenterUserLocation);
  const initialUserOfficeLocationRef = useRef(userOfficeLocation);
  const initialSalesOfficeOptionsRef = useRef(salesOfficeOptions);
  const initialSectionOptionsRef = useRef(sectionOptions);
  const initialSquadOptionsRef = useRef(squadOptions);
  const [showMissingBranch, setShowMissingBranch] = useState(false);
  const [showMissingSalesOffice, setShowMissingSalesOffice] = useState(false);

  const { user } = useWaUserStore();

  const reservedOptions = [
    { title: t('wards.common.reservationStatus.all'), value: '' },
    { title: t('wards.common.reservationStatus.reserved'), value: 'true' },
    { title: t('wards.common.reservationStatus.unreserved'), value: 'false' },
  ];

  const navigate = useNavigate();

  const runValidation = () => {
    // To handle Okinichi, need to allow '  ' as branch code
    const isBranchSelected =
      !!workplaceFilterParameters.branchCode && workplaceFilterParameters.branchCode.length > 0;

    const isCustomerNumberEntered =
      !!workplaceFilterParameters.customerId &&
      workplaceFilterParameters.customerId.trim().length > 0;

    const isSalesOfficeSelected =
      !!workplaceFilterParameters.salesOfficeCode &&
      workplaceFilterParameters.salesOfficeCode.trim().length > 0;

    setShowMissingBranch(false);
    setShowMissingSalesOffice(false);

    if (!isBranchSelected) {
      setShowMissingBranch(true);
    }

    if (!isCustomerNumberEntered && !isSalesOfficeSelected) {
      setShowMissingSalesOffice(true);
    }

    if (isBranchSelected && (isSalesOfficeSelected || isCustomerNumberEntered)) {
      return true;
    }

    return false;
  };

  const handleMapDisplayAction = () => {
    if (!runValidation()) {
      return;
    }

    wardsNavigate(navigate, WaScreensMap[WA_Screen.WorkplacesMap].path);
  };

  const handleListDisplayAction = () => {
    if (!runValidation()) {
      return;
    }

    wardsNavigate(navigate, WaScreensMap[WA_Screen.UnadressedWorkplacesList].path);
  };

  const handleResetAction = () => {
    patchWorkplaceFilterParameters({
      reserved: false,
      branchCode: user?.branchCode || '',
      salesOfficeCode: user?.salesOfficeCode || '',
      sectionCode: user?.sectionCode || '',
      squadCode: user?.squadCode || '',
      statusList: OperationStatus.Incomplete,
      customerId: '',
    });
  };

  const handleCancelAction = () => {
    patchWorkplaceFilterParameters(initialFilterParamsRef.current);
    setSalesOfficeOptions(initialSalesOfficeOptionsRef.current);
    setSectionOptions(initialSectionOptionsRef.current);
    setSquadOptions(initialSquadOptionsRef.current);
    setMapCenter(initialMapCenterRef.current);
    setMapCenterUserLocation(initialMapCenterUserLocationRef.current);
    setUserOfficeLocation(initialUserOfficeLocationRef.current);
    setFilterChanged(false);
    navigate(-1);
  };

  const handleReservationStatusChanged = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    patchWorkplaceFilterParameters({
      reserved: value === '' ? undefined : value === 'true',
    });
  };

  useEffect(() => {
    if (workplaceFilterParameters.branchCode || !user?.branchCode || filtersInitialized) return;

    patchWorkplaceFilterParameters({
      reserved: false,
      branchCode: user?.branchCode,
      salesOfficeCode: user?.salesOfficeCode,
      sectionCode: user?.sectionCode,
      squadCode: user?.squadCode,
      statusList: OperationStatus.Incomplete,
      customerId: '',
    });

    setFiltersInitialized(true);
  }, [
    filtersInitialized,
    patchWorkplaceFilterParameters,
    setFiltersInitialized,
    user,
    workplaceFilterParameters.branchCode,
  ]);

  useEffect(() => {
    // Decide office location on map. Office location is stored in Sales Office or Section.
    const salesOfficeCode = workplaceFilterParameters.salesOfficeCode;
    const sectionCode = workplaceFilterParameters.sectionCode;
    const salesOffice = salesOfficeOptions.find((office) => office.code === salesOfficeCode);
    const section = sectionOptions.find((section) => section.code === sectionCode);
    const officeLocation = section?.location ?? salesOffice?.location ?? null;
    setUserOfficeLocation(officeLocation);

    /**
     * Mobile Map Centering Logic:
     * 1. Searched with Organization, Selected "Centering on Sales Office" -> Center on Sales Office (or Section)
     * 2. Searched with Organization, Selected "Centering on User Location" -> Center on User Location
     * 3. Searched with Customer -> Center on Customer Location
     *
     * Here, we can handle 1 and 2. 3 will be handled in Map.tsx,
     * because we need to fetch customer location based on customerId in filter parameters.
     */
    if (mapCenterUserLocation) {
      setMapCenter(user?.location ?? defaultMapCenter);
    } else {
      setMapCenter(officeLocation ? officeLocation : defaultMapCenter);
    }
  }, [
    mapCenterUserLocation,
    setMapCenter,
    user?.location,
    salesOfficeOptions,
    sectionOptions,
    workplaceFilterParameters,
    setUserOfficeLocation,
  ]);

  useEffect(() => {
    fetchBranchOptions();
  }, [fetchBranchOptions]);

  useEffect(() => {
    const branchCode = workplaceFilterParameters.branchCode;

    if (!branchCode) return;

    fetchSalesOfficeOptions(branchCode);
  }, [fetchSalesOfficeOptions, workplaceFilterParameters.branchCode]);

  useEffect(() => {
    const branchCode = workplaceFilterParameters.branchCode;
    const salesOfficeCode = workplaceFilterParameters.salesOfficeCode;

    if (!branchCode || !salesOfficeCode) {
      setSectionOptions([]);
      setSquadOptions([]);
      return;
    }

    fetchSectionOptions(branchCode, salesOfficeCode);
  }, [
    fetchSectionOptions,
    setSectionOptions,
    setSquadOptions,
    workplaceFilterParameters.branchCode,
    workplaceFilterParameters.salesOfficeCode,
  ]);

  useEffect(() => {
    const branchCode = workplaceFilterParameters.branchCode;
    const salesOfficeCode = workplaceFilterParameters.salesOfficeCode;
    const sectionCode = workplaceFilterParameters.sectionCode;

    if (!branchCode || !salesOfficeCode || !sectionCode) {
      setSquadOptions([]);
      return;
    }

    fetchSquadOptions(branchCode, salesOfficeCode, sectionCode);
  }, [
    fetchSquadOptions,
    setSquadOptions,
    workplaceFilterParameters.branchCode,
    workplaceFilterParameters.salesOfficeCode,
    workplaceFilterParameters.sectionCode,
  ]);

  useEffect(() => {
    fetchStatusOptions();
  }, [fetchStatusOptions]);

  const getStatusTranslation = (key: string): string => {
    const translationKey = `wards.common.statusCodes.${key}`;
    const translatedValue = t(translationKey);
    return translatedValue === translationKey ? key : translatedValue;
  };

  const customStatusOptions = [
    {
      groupName: OperationStatus.Incomplete,
      statuses: operationStatusCodes(OperationStatus.Incomplete),
    },
    { groupName: OperationStatus.None, statuses: operationStatusCodes(OperationStatus.None) },
  ];

  const fullStatusOptions = [...statusOptions, ...customStatusOptions];

  const statusDropdownOptions: OptionProps[] = sortStatusOptions(
    fullStatusOptions,
    'groupName',
    defaultSatusOrder
  ).map((group) => ({
    title: getStatusTranslation(group.groupName),
    value: group.groupName,
  }));

  const selectedStatusGroup = fullStatusOptions.find(
    (group) => group.groupName === workplaceFilterParameters.statusList
  );

  const handleStateChanged = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedGroupName = event.target.value;

    if (!selectedGroupName) {
      patchWorkplaceFilterParameters({ statusList: OperationStatus.Incomplete });
      return;
    }

    const selectedGroup = fullStatusOptions.find((group) => group.groupName === selectedGroupName);

    patchWorkplaceFilterParameters({
      statusList: selectedGroup?.groupName,
    });
  };

  const branchDropdownOptions: OptionProps[] = [
    { title: t('wards.common.none'), value: '' },
    ...branchOptions.map((b) => ({ title: b.code + ' ' + b.name, value: b.code })),
  ];
  const salesOfficeDropdownOptions: OptionProps[] = [
    { title: t('wards.common.none'), value: '' },
    ...salesOfficeOptions.map((b) => ({ title: b.code + ' ' + b.name, value: b.code })),
  ];
  const sectionDropdownOptions: OptionProps[] = [
    { title: t('wards.common.none'), value: '' },
    ...sectionOptions.map((b) => ({ title: b.code + ' ' + b.name, value: b.code })),
  ];
  const squadDropdownOptions: OptionProps[] = [
    { title: t('wards.common.none'), value: '' },
    ...squadOptions.map((b) => ({ title: b.code, value: b.code })),
  ];

  const selectedBranchOption = branchDropdownOptions.find(
    (opt) => opt.value === workplaceFilterParameters.branchCode
  );
  const selectedSalesOfficeOption = salesOfficeDropdownOptions.find(
    (option) => option.value === workplaceFilterParameters.salesOfficeCode
  );
  const selectedSectionOption = sectionDropdownOptions.find(
    (option) => option.value === workplaceFilterParameters.sectionCode
  );
  const selectedSquadOption = squadDropdownOptions.find(
    (option) => option.value === workplaceFilterParameters.squadCode
  );
  const selectedReservedOption = reservedOptions.find(
    (opt) =>
      opt.value ===
      (workplaceFilterParameters.reserved === undefined
        ? ''
        : String(workplaceFilterParameters.reserved))
  );

  return (
    <div className="flex min-h-screen flex-col space-y-4 bg-gray-100 px-4 py-8">
      <h2 className="mb-4 text-left text-2xl font-bold">{t('wards.mapFilter.title')}</h2>
      <div className="space-y-4">
        <FormRow
          title={t('wards.common.branchOffice')}
          required
          layout="vertical"
          showError={showMissingBranch}
          errorMessage={t('wards.sites.missingBranchOffice')}
        >
          <DropdownSelect
            selectedValue={selectedBranchOption}
            optionValues={branchDropdownOptions}
            onChange={(event) => {
              const val = event.target.value;
              if (val !== '') {
                setShowMissingBranch(false);
              }
              patchWorkplaceFilterParameters({
                branchCode: val,
                salesOfficeCode: '',
                sectionCode: '',
                squadCode: '',
              });
            }}
          />
        </FormRow>
        <FormRow
          title={t('wards.common.salesOffice')}
          required
          layout="vertical"
          showError={showMissingSalesOffice}
          errorMessage={t('wards.sites.missingSalesOffice')}
        >
          <DropdownSelect
            selectedValue={selectedSalesOfficeOption}
            optionValues={salesOfficeDropdownOptions}
            onChange={(event) => {
              const val = event.target.value;
              if (val !== '') {
                setShowMissingSalesOffice(false);
              }
              patchWorkplaceFilterParameters({
                salesOfficeCode: val,
                sectionCode: '',
                squadCode: '',
              });
            }}
          />
        </FormRow>
        <FormRow title={t('wards.common.section')} layout="vertical">
          <DropdownSelect
            selectedValue={selectedSectionOption}
            optionValues={sectionDropdownOptions}
            onChange={(event) => {
              patchWorkplaceFilterParameters({
                sectionCode: event.target.value,
                squadCode: '',
              });
            }}
          />
        </FormRow>
        <FormRow title={t('wards.common.squad')} layout="vertical">
          <DropdownSelect
            selectedValue={selectedSquadOption}
            optionValues={squadDropdownOptions}
            onChange={(event) => {
              patchWorkplaceFilterParameters({
                squadCode: event.target.value,
              });
            }}
          />
        </FormRow>
        <FormRow title={t('wards.common.status')} layout="vertical">
          <DropdownSelect
            selectedValue={
              selectedStatusGroup
                ? {
                    title: getStatusTranslation(selectedStatusGroup.groupName),
                    value: selectedStatusGroup.groupName,
                  }
                : { title: t('wards.common.none'), value: '' }
            }
            optionValues={statusDropdownOptions}
            onChange={handleStateChanged}
          />
        </FormRow>
        <FormRow title={t('wards.mapFilter.maintenanceStatus')} layout="vertical">
          <DropdownSelect
            selectedValue={selectedReservedOption}
            optionValues={reservedOptions}
            onChange={handleReservationStatusChanged}
          />
        </FormRow>
        <FormRow
          title={t('wards.common.customerNumber')}
          layout="vertical"
          required
          showError={showMissingSalesOffice}
          errorMessage={t('wards.sites.missingSalesOffice')}
        >
          <Input
            value={workplaceFilterParameters.customerId ?? ''}
            onChange={(event) => {
              setShowMissingSalesOffice(false);
              patchWorkplaceFilterParameters({
                customerId: event.target.value,
              });
            }}
          />
        </FormRow>
        <p className="text-xs text-gray-500">{t('wards.mapFilter.warningLabel')}</p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Button title={t('wards.mapFilter.mapDisplay')} onClick={handleMapDisplayAction} />
        <Button title={t('wards.mapFilter.listDisplay')} onClick={handleListDisplayAction} />
      </div>
      <div className="flex justify-around gap-1 pb-8 pt-2">
        <label className="flex space-x-2">
          <input
            type="radio"
            name="mapLocation"
            value="current"
            checked={mapCenterUserLocation}
            onClick={() => setMapCenterUserLocation(true)}
            onChange={() => {}}
          />
          <span className="text-sm">{t('wards.mapFilter.radioOptionCurrentLocation')}</span>
        </label>
        <label className="flex space-x-2">
          <input
            type="radio"
            name="mapLocation"
            value="salesOffice"
            checked={!mapCenterUserLocation}
            onClick={() => setMapCenterUserLocation(false)}
            onChange={() => {}}
          />
          <span className="text-sm">{t('wards.mapFilter.radioOptionBussnessOffice')}</span>
        </label>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Button title={t('wards.mapFilter.reset')} onClick={handleResetAction} />
        <Button title={t('wards.common.cancel')} onClick={handleCancelAction} />
      </div>
    </div>
  );
}
